import { Box, Button, ButtonProps, ButtonPropsColorOverrides, Typography } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { useEffect, useRef, useState } from 'react';

type PaletteKey = OverridableStringUnion<
    'error' | 'primary' | 'secondary' | 'warning' | 'success' | 'info',
    ButtonPropsColorOverrides
>;

interface HoldTimeProps extends Omit<ButtonProps, 'color'> {
    holdTime: number;
    holdBackgrondPalleteKey?: PaletteKey;
    holdText?: string;
    color?: PaletteKey;
    onHoldComplete: () => void;
    iconOnHold?: boolean;
}

const HoldButton = ({
    holdTime = 3000,
    holdBackgrondPalleteKey = 'primary-300',
    color = 'primary',
    onHoldComplete,
    iconOnHold,
    startIcon,
    holdText,
    ...props
}: HoldTimeProps) => {
    const intervalTime = 50; // Interval update for smooth fill
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number | null>(null);

    const startHold = () => {
        if (intervalRef.current) return; // Prevent multiple intervals
        startTimeRef.current = Date.now();
        intervalRef.current = setInterval(() => {
            const elapsedTime = Date.now() - (startTimeRef.current ?? 0);
            const newProgress = (elapsedTime / holdTime) * 100;
            setProgress(Math.min(newProgress, 100));

            if (elapsedTime >= holdTime) {
                completeHold();
            }
        }, intervalTime);
    };

    const stopHold = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setProgress(0);
    };

    const completeHold = () => {
        stopHold();
        onHoldComplete();
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return (
        <Button
            onMouseDown={startHold}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={startHold}
            onTouchEnd={stopHold}
            onClick={(e) => e.stopPropagation()}
            disableRipple
            startIcon={iconOnHold ? (progress > 0 ? startIcon : null) : startIcon}
            {...props}
            color={color}
            sx={{
                '&:hover': {
                    backgroundColor: (theme) => theme.palette[color as PaletteKey].main,
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: (theme) => theme.palette[holdBackgrondPalleteKey].main, // Progress bar color
                    transition: 'width 50ms linear',
                    borderRadius: 1,
                }}
            />
            <div className="z-10">
                {progress > 0 ? (
                    <Typography fontFamily="Roboto" fontWeight={500} variant="body2" lineHeight="22px">
                        {holdText ?? 'HOLD'}
                    </Typography>
                ) : (
                    props.children
                )}
            </div>
        </Button>
    );
};

export { HoldButton };
